import { useState } from 'react'
import { Bell, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MarketingShell } from './MarketingShell'

export function AdminCampaigns() {
  const [notified, setNotified] = useState(false)

  return (
    <MarketingShell
      title="Campaigns"
      description="Segment customers, send a targeted offer, and watch redemptions and revenue land back in the dashboard — all in one place."
    >
      <style>{TEASER_CSS}</style>
      <div className="bhk-campaigns-teaser -m-6">
        <div className="bhk-teaser-stage">
          <div className="bhk-teaser-bg" aria-hidden />
          <div className="bhk-teaser-grain" aria-hidden />
          <div className="bhk-teaser-grid" aria-hidden />
          <div className="bhk-teaser-glow bhk-teaser-glow--a" aria-hidden />
          <div className="bhk-teaser-glow bhk-teaser-glow--b" aria-hidden />

          <div className="bhk-teaser-content">
            {/* Top: text on left, mock card on right */}
            <div className="bhk-teaser-hero">
              <div className="bhk-teaser-hero-text">
                <span
                  className="bhk-teaser-pill bhk-anim"
                  style={{ ['--d' as any]: '0ms' }}
                >
                  <span className="bhk-teaser-pill-dot" />
                  Arriving this season
                </span>

                <h2
                  className="bhk-teaser-headline bhk-anim"
                  style={{ ['--d' as any]: '80ms' }}
                >
                  Reach the right table.
                  <br />
                  <span className="bhk-teaser-headline-accent">
                    Right when it matters.
                  </span>
                </h2>

                <p
                  className="bhk-teaser-lead bhk-anim"
                  style={{ ['--d' as any]: '160ms' }}
                >
                  Build a segment, hand it a promo code, send by email or
                  WhatsApp — then watch every redemption walk back into the
                  dashboard, attached to the blast that earned it.
                </p>
              </div>

              <div className="bhk-teaser-hero-visual">
                <MockCampaignCard />
              </div>
            </div>

            {/* Editorial pillars */}
            <ol className="bhk-teaser-pillars">
              {PILLARS.map((p, i) => (
                <li
                  key={p.title}
                  className="bhk-teaser-pillar bhk-anim"
                  style={{ ['--d' as any]: `${340 + i * 70}ms` }}
                >
                  <span className="bhk-teaser-pillar-num">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="bhk-teaser-pillar-text">
                    <h3>{p.title}</h3>
                    <p>{p.body}</p>
                  </div>
                </li>
              ))}
            </ol>

            {/* Centered CTA */}
            <div
              className="bhk-teaser-cta-row bhk-anim"
              style={{ ['--d' as any]: '660ms' }}
            >
              <button
                type="button"
                onClick={() => setNotified(true)}
                disabled={notified}
                className={cn('bhk-teaser-cta', notified && 'is-done')}
              >
                {notified ? (
                  <>
                    <Check className="h-5 w-5" aria-hidden />
                    We'll let you know
                  </>
                ) : (
                  <>
                    <Bell className="h-5 w-5" aria-hidden />
                    Notify me when it launches
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MarketingShell>
  )
}

const PILLARS = [
  {
    title: 'Smart segments',
    body: 'Birthday, lapsed, top spenders — or your own rules over visit history and basket size.',
  },
  {
    title: 'Email + WhatsApp + SMS',
    body: 'Queued email through the outbox worker, plus click-to-send WhatsApp and SMS from the manager device.',
  },
  {
    title: 'Promo-code attribution',
    body: 'Every send carries a tracked code, so each redemption ties back to the campaign that drove it.',
  },
  {
    title: 'Revenue tracking',
    body: 'Sent → opened → clicked → redeemed → PKR earned, surfaced on every campaign card.',
  },
] as const

function MockCampaignCard() {
  return (
    <div
      className="bhk-teaser-mock bhk-anim"
      style={{ ['--d' as any]: '200ms' }}
    >
      <div className="bhk-teaser-mock-glow" aria-hidden />
      <div className="bhk-teaser-mock-card">
        <div className="bhk-teaser-mock-head">
          <div className="bhk-teaser-mock-status">
            <span className="bhk-teaser-mock-status-dot" />
            Sent · 2 days ago
          </div>
          <div className="bhk-teaser-mock-name">Bring back Friday regulars</div>
          <div className="bhk-teaser-mock-sub">
            412 customers we haven't seen in 30 days
          </div>
        </div>

        <div className="bhk-teaser-mock-metrics">
          <Metric label="Opened" value="68%" sub="282 / 412" />
          <Metric label="Clicked" value="41%" sub="115 redirects" />
          <Metric label="Redeemed" value="149" sub="code WIN15" />
          <Metric
            label="Earned"
            value={
              <>
                <span className="bhk-teaser-metric-ccy">PKR</span> 420,000
              </>
            }
            sub="avg basket 2,150"
            emphasis
          />
        </div>

        <div className="bhk-teaser-mock-foot">
          <Sparkline />
          <span>Last 28 days</span>
        </div>
      </div>
    </div>
  )
}

function Metric({
  label,
  value,
  sub,
  emphasis,
}: {
  label: string
  value: React.ReactNode
  sub: string
  emphasis?: boolean
}) {
  return (
    <div
      className={cn(
        'bhk-teaser-metric',
        emphasis && 'bhk-teaser-metric--emphasis',
      )}
    >
      <span className="bhk-teaser-metric-label">{label}</span>
      <span className="bhk-teaser-metric-value">{value}</span>
      <span className="bhk-teaser-metric-sub">{sub}</span>
    </div>
  )
}

function Sparkline() {
  const points = [
    2, 3, 4, 6, 5, 7, 9, 8, 6, 8, 11, 10, 12, 14, 18, 16, 19, 22, 28, 27, 25,
    32, 35, 30, 38, 42, 40, 48,
  ]
  const max = Math.max(...points)
  const w = 200
  const h = 36
  const step = w / (points.length - 1)
  const d = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'} ${i * step} ${h - (p / max) * (h - 4) - 2}`,
    )
    .join(' ')
  const lastX = (points.length - 1) * step
  const lastY = h - (points[points.length - 1] / max) * (h - 4) - 2
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="bhk-teaser-sparkline"
      aria-hidden
    >
      <defs>
        <linearGradient id="bhk-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#ea580c" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L ${w} ${h} L 0 ${h} Z`} fill="url(#bhk-spark-fill)" />
      <path d={d} fill="none" stroke="#fbbf24" strokeWidth="1.5" />
      <circle cx={lastX} cy={lastY} r="2.5" fill="#fbbf24" />
      <circle cx={lastX} cy={lastY} r="5" fill="#fbbf24" opacity="0.25" />
    </svg>
  )
}

const TEASER_CSS = `
.bhk-campaigns-teaser {
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
}
.bhk-campaigns-teaser .bhk-teaser-stage {
  position: relative;
  overflow: hidden;
  isolation: isolate;
  background: #1a0d05;
  color: #fdf8f1;
  padding: clamp(2rem, 4.5vw, 3.75rem) clamp(1.25rem, 4vw, 3.5rem) clamp(2rem, 4vw, 3.25rem);
  border-bottom-left-radius: var(--radius, 0.75rem);
  border-bottom-right-radius: var(--radius, 0.75rem);
}
.bhk-campaigns-teaser .bhk-teaser-bg {
  position: absolute; inset: 0; z-index: -3;
  background:
    radial-gradient(circle at 18% 12%, rgba(255, 200, 120, 0.32) 0%, transparent 42%),
    radial-gradient(circle at 88% 6%, rgba(244, 63, 94, 0.26) 0%, transparent 38%),
    radial-gradient(circle at 50% 110%, rgba(180, 83, 9, 0.4) 0%, transparent 55%),
    linear-gradient(140deg, #2a1407 0%, #4a1d05 45%, #3a1608 100%);
}
.bhk-campaigns-teaser .bhk-teaser-grain {
  position: absolute; inset: 0; z-index: -2; pointer-events: none;
  background-image:
    radial-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    radial-gradient(rgba(0, 0, 0, 0.06) 1px, transparent 1px);
  background-size: 3px 3px, 5px 5px;
  background-position: 0 0, 1px 2px;
  opacity: 0.7;
}
.bhk-campaigns-teaser .bhk-teaser-grid {
  position: absolute; inset: 0; z-index: -2; pointer-events: none;
  background-image:
    linear-gradient(rgba(253, 248, 241, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(253, 248, 241, 0.045) 1px, transparent 1px);
  background-size: 64px 64px;
  -webkit-mask-image: radial-gradient(ellipse 80% 60% at 50% 35%, black 0%, transparent 75%);
          mask-image: radial-gradient(ellipse 80% 60% at 50% 35%, black 0%, transparent 75%);
}
.bhk-campaigns-teaser .bhk-teaser-glow {
  position: absolute; border-radius: 50%; filter: blur(80px);
  z-index: -1; pointer-events: none; opacity: 0.55;
  will-change: transform;
}
.bhk-campaigns-teaser .bhk-teaser-glow--a {
  width: 460px; height: 460px; top: -140px; right: -100px;
  background: radial-gradient(circle, rgba(249, 115, 22, 0.65), transparent 70%);
  animation: bhkDriftA 22s ease-in-out infinite;
}
.bhk-campaigns-teaser .bhk-teaser-glow--b {
  width: 520px; height: 520px; bottom: -160px; left: -120px;
  background: radial-gradient(circle, rgba(225, 29, 72, 0.45), transparent 70%);
  animation: bhkDriftB 26s ease-in-out infinite;
}
@keyframes bhkDriftA {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%      { transform: translate(-50px, 40px) scale(1.12); }
}
@keyframes bhkDriftB {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%      { transform: translate(60px, -40px) scale(1.08); }
}

.bhk-campaigns-teaser .bhk-teaser-content {
  position: relative;
  max-width: 78rem;
  margin: 0 auto;
}

/* ── Entrance animation utility ────────────────────────────────── */
.bhk-campaigns-teaser .bhk-anim {
  opacity: 0;
  transform: translateY(14px);
  animation: bhkRise 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  animation-delay: var(--d, 0ms);
}
@keyframes bhkRise {
  to { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .bhk-campaigns-teaser .bhk-anim,
  .bhk-campaigns-teaser .bhk-teaser-glow,
  .bhk-campaigns-teaser .bhk-teaser-mock,
  .bhk-campaigns-teaser .bhk-teaser-pill-dot {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}

/* ── Hero layout (text left, visual right) ────────────────────── */
.bhk-campaigns-teaser .bhk-teaser-hero {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  align-items: center;
}
@media (min-width: 960px) {
  .bhk-campaigns-teaser .bhk-teaser-hero {
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
    gap: 3rem;
  }
}
.bhk-campaigns-teaser .bhk-teaser-hero-text { min-width: 0; }
.bhk-campaigns-teaser .bhk-teaser-hero-visual { min-width: 0; }

/* ── Pill ──────────────────────────────────────────────────────── */
.bhk-campaigns-teaser .bhk-teaser-pill {
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.375rem 0.875rem 0.375rem 0.625rem;
  border-radius: 9999px;
  background: rgba(253, 248, 241, 0.07);
  border: 1px solid rgba(253, 248, 241, 0.16);
  font-size: 0.6875rem; font-weight: 500;
  letter-spacing: 0.12em; text-transform: uppercase;
  color: rgba(253, 248, 241, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.bhk-campaigns-teaser .bhk-teaser-pill-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 0 14px rgba(251, 191, 36, 0.9);
  animation: bhkPulse 1.8s ease-in-out infinite;
}
@keyframes bhkPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.35; transform: scale(0.6); }
}

/* ── Headline ──────────────────────────────────────────────────── */
.bhk-campaigns-teaser .bhk-teaser-headline {
  font-family: 'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif;
  font-weight: 400;
  font-size: clamp(2.25rem, 4.6vw, 4rem);
  line-height: 0.98;
  letter-spacing: -0.02em;
  margin: 1.125rem 0 0;
  color: #fdf8f1;
  text-wrap: balance;
}
.bhk-campaigns-teaser .bhk-teaser-headline-accent {
  font-style: italic;
  background: linear-gradient(180deg, #fde68a 0%, #fbbf24 35%, #f97316 70%, #ea580c 100%);
  -webkit-background-clip: text;
          background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

/* ── Lead ──────────────────────────────────────────────────────── */
.bhk-campaigns-teaser .bhk-teaser-lead {
  font-size: 1.0625rem;
  line-height: 1.55;
  color: rgba(253, 248, 241, 0.74);
  margin: 1rem 0 0;
  max-width: 36rem;
}

/* ── Mock campaign card ───────────────────────────────────────── */
.bhk-campaigns-teaser .bhk-teaser-mock {
  position: relative;
  max-width: 36rem;
  margin: 0 auto;
  animation: bhkRise 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards,
             bhkFloat 7s ease-in-out 1s infinite;
  animation-delay: var(--d, 0ms);
  opacity: 0;
  transform: translateY(14px);
}
@keyframes bhkFloat {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
}
.bhk-campaigns-teaser .bhk-teaser-mock-glow {
  position: absolute; inset: -28px;
  background: radial-gradient(ellipse at 30% 0%, rgba(249, 115, 22, 0.45), transparent 65%);
  filter: blur(28px);
  z-index: -1;
  pointer-events: none;
}
.bhk-campaigns-teaser .bhk-teaser-mock-card {
  position: relative;
  border-radius: 1.25rem;
  padding: 1.5rem;
  background:
    linear-gradient(180deg, rgba(253, 248, 241, 0.085) 0%, rgba(253, 248, 241, 0.035) 100%);
  border: 1px solid rgba(253, 248, 241, 0.14);
  backdrop-filter: blur(20px) saturate(140%);
  -webkit-backdrop-filter: blur(20px) saturate(140%);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.08) inset,
    0 30px 60px -25px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(0, 0, 0, 0.15);
}
.bhk-campaigns-teaser .bhk-teaser-mock-head {
  display: flex; flex-direction: column; gap: 0.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(253, 248, 241, 0.08);
  margin-bottom: 1rem;
}
.bhk-campaigns-teaser .bhk-teaser-mock-status {
  display: inline-flex; align-items: center; gap: 0.4rem;
  font-size: 0.6875rem;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(253, 248, 241, 0.55);
  align-self: flex-start;
}
.bhk-campaigns-teaser .bhk-teaser-mock-status-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 8px rgba(52, 211, 153, 0.8);
}
.bhk-campaigns-teaser .bhk-teaser-mock-name {
  font-family: 'Instrument Serif', 'Iowan Old Style', Georgia, serif;
  font-size: 1.5rem;
  line-height: 1.1;
  letter-spacing: -0.015em;
  margin-top: 0.25rem;
  color: #fdf8f1;
}
.bhk-campaigns-teaser .bhk-teaser-mock-sub {
  font-size: 0.8125rem;
  color: rgba(253, 248, 241, 0.6);
}

.bhk-campaigns-teaser .bhk-teaser-mock-metrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.875rem 1rem;
}
@media (min-width: 540px) {
  .bhk-campaigns-teaser .bhk-teaser-mock-metrics {
    grid-template-columns: repeat(4, 1fr);
  }
}
.bhk-campaigns-teaser .bhk-teaser-metric {
  display: flex; flex-direction: column; gap: 0.15rem;
  padding: 0.5rem 0 0.5rem 0.75rem;
  border-left: 1px solid rgba(253, 248, 241, 0.1);
}
.bhk-campaigns-teaser .bhk-teaser-metric--emphasis {
  border-left-color: rgba(251, 191, 36, 0.55);
}
.bhk-campaigns-teaser .bhk-teaser-metric-label {
  font-size: 0.6875rem;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: rgba(253, 248, 241, 0.5);
}
.bhk-campaigns-teaser .bhk-teaser-metric-value {
  font-family: 'Instrument Serif', 'Iowan Old Style', Georgia, serif;
  font-size: 1.5rem;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: #fdf8f1;
  margin-top: 0.1rem;
}
.bhk-campaigns-teaser .bhk-teaser-metric--emphasis .bhk-teaser-metric-value {
  background: linear-gradient(180deg, #fde68a 0%, #fbbf24 50%, #f97316 100%);
  -webkit-background-clip: text;
          background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}
.bhk-campaigns-teaser .bhk-teaser-metric-ccy {
  font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  vertical-align: 0.25em;
  margin-right: 0.125rem;
  -webkit-text-fill-color: currentColor;
  background: none;
  color: rgba(251, 191, 36, 0.85);
}
.bhk-campaigns-teaser .bhk-teaser-metric-sub {
  font-size: 0.6875rem;
  color: rgba(253, 248, 241, 0.45);
  font-variant-numeric: tabular-nums;
}

.bhk-campaigns-teaser .bhk-teaser-mock-foot {
  margin-top: 1rem;
  padding-top: 0.875rem;
  border-top: 1px solid rgba(253, 248, 241, 0.08);
  display: flex; align-items: center; justify-content: space-between;
  gap: 1rem;
}
.bhk-campaigns-teaser .bhk-teaser-sparkline {
  flex: 1;
  height: 36px;
  max-width: 240px;
}
.bhk-campaigns-teaser .bhk-teaser-mock-foot > span {
  font-size: 0.6875rem;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(253, 248, 241, 0.45);
  white-space: nowrap;
}

/* ── Pillars (editorial numbered list, full width) ────────────── */
.bhk-campaigns-teaser .bhk-teaser-pillars {
  margin: 2.5rem 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem 2.5rem;
}
@media (min-width: 640px) {
  .bhk-campaigns-teaser .bhk-teaser-pillars {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (min-width: 1100px) {
  .bhk-campaigns-teaser .bhk-teaser-pillars {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.25rem 2rem;
  }
}
.bhk-campaigns-teaser .bhk-teaser-pillar {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.875rem;
  align-items: baseline;
  padding-top: 1.125rem;
  border-top: 1px solid rgba(253, 248, 241, 0.12);
}
.bhk-campaigns-teaser .bhk-teaser-pillar-num {
  font-family: 'Instrument Serif', 'Iowan Old Style', Georgia, serif;
  font-style: italic;
  font-size: 1.625rem;
  line-height: 1;
  color: rgba(251, 191, 36, 0.85);
  font-variant-numeric: oldstyle-nums;
  min-width: 2rem;
}
.bhk-campaigns-teaser .bhk-teaser-pillar-text h3 {
  font-size: 0.95rem;
  font-weight: 600;
  color: #fdf8f1;
  margin: 0 0 0.25rem;
  letter-spacing: -0.005em;
}
.bhk-campaigns-teaser .bhk-teaser-pillar-text p {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: rgba(253, 248, 241, 0.62);
  margin: 0;
}

/* ── CTA (centered, prominent) ────────────────────────────────── */
.bhk-campaigns-teaser .bhk-teaser-cta-row {
  margin: 2.75rem 0 0;
  display: flex; justify-content: center;
}
.bhk-campaigns-teaser .bhk-teaser-cta {
  display: inline-flex; align-items: center; gap: 0.625rem;
  padding: 1rem 1.875rem;
  border-radius: 9999px;
  font-size: 1rem; font-weight: 600;
  letter-spacing: -0.005em;
  color: #2a1407;
  background: linear-gradient(180deg, #fde68a 0%, #fbbf24 50%, #f97316 100%);
  border: none;
  cursor: pointer;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.5) inset,
    0 0 0 1px rgba(124, 45, 18, 0.4),
    0 18px 36px -10px rgba(234, 88, 12, 0.6);
  transition: transform 0.18s ease, box-shadow 0.2s ease, filter 0.2s ease;
}
.bhk-campaigns-teaser .bhk-teaser-cta:hover:not(:disabled) {
  filter: brightness(1.06);
  transform: translateY(-1px);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.55) inset,
    0 0 0 1px rgba(124, 45, 18, 0.5),
    0 26px 44px -12px rgba(234, 88, 12, 0.7);
}
.bhk-campaigns-teaser .bhk-teaser-cta:active:not(:disabled) {
  transform: translateY(0);
}
.bhk-campaigns-teaser .bhk-teaser-cta.is-done {
  cursor: default;
  background: rgba(253, 248, 241, 0.1);
  color: #fdf8f1;
  border: 1px solid rgba(253, 248, 241, 0.2);
  box-shadow: none;
}
`
